import { Controller } from '@hotwired/stimulus';
import Trix from 'trix';
import AutoSave from 'stimulus/auto_save';

import safeLinkUrl from './link_url';

if (!Trix.config.textAttributes.underline) {
  Trix.config.textAttributes.underline = { tagName: 'u', inheritable: true };
}

['heading2', 'heading3', 'heading4'].forEach((name, index) => {
  if (!Trix.config.blockAttributes[name]) {
    Trix.config.blockAttributes[name] = {
      tagName: `h${index + 2}`,
      terminal: true,
      breakOnReturn: true,
      group: false,
    };
  }
});

const HEADINGS = ['heading1', 'heading2', 'heading3', 'heading4'];

export default class extends Controller {
  static targets = ['editor', 'formatTrigger', 'formatIcon', 'formatLabel', 'formatMenu', 'formatOption',
    'toolbarToggle', 'linkButton', 'linkPopover', 'linkText', 'linkUrl'];

  static values = {
    disabled: Boolean,
    autoSaveUrl: String,
    debounce: { type: Number, default: 500 }
  };

  connect() {
    this.syncFormat = this.syncFormat.bind(this);
    this.announceChange = this.announceChange.bind(this);
    this.closeOnOutside = this.closeOnOutside.bind(this);
    this.closeLinkOnOutside = this.closeLinkOnOutside.bind(this);
    this.disable = this.disable.bind(this);
    this.save = this.save.bind(this);
    if (!this.hasEditorTarget) return;

    this.autoSave = new AutoSave({
      url: this.autoSaveUrlValue,
      name: this.hiddenInput()?.name,
      debounce: this.debounceValue,
      label: this.identifier,
      element: this.element
    });

    this.editorTarget.addEventListener('trix-selection-change', this.syncFormat);
    this.editorTarget.addEventListener('trix-change', this.announceChange);
    this.editorTarget.addEventListener('trix-blur', this.save);
    this.toggleObserver = new MutationObserver(() => this.syncToolbarToggles());
    this.toggleObserver.observe(this.element, {
      subtree: true, attributes: true, attributeFilter: ['data-trix-active']
    });
    this.labelShortcuts();
    if (this.disabledValue) {
      this.editorTarget.addEventListener('trix-initialize', this.disable);
      this.disable();
    }
  }

  disconnect() {
    if (this.hasEditorTarget) {
      this.editorTarget.removeEventListener('trix-selection-change', this.syncFormat);
      this.editorTarget.removeEventListener('trix-change', this.announceChange);
      this.editorTarget.removeEventListener('trix-blur', this.save);
      this.editorTarget.removeEventListener('trix-initialize', this.disable);
      this.autoSave?.cancel();
    }
    if (this.toggleObserver) this.toggleObserver.disconnect();
    document.removeEventListener('click', this.closeOnOutside);
    document.removeEventListener('click', this.closeLinkOnOutside);
  }

  // Trix's hidden input stays silent when Trix writes to it, so pass its change on.
  announceChange() {
    this.dirty = true;
    this.hiddenInput()?.dispatchEvent(new Event('change', { bubbles: true }));
  }

  save() {
    if (!this.autoSaveUrlValue || !this.dirty) return;

    this.dirty = false;
    this.autoSave.saveNow(this.hiddenInput()?.value ?? '');
  }

  hiddenInput() {
    return this.element.querySelector(`#${CSS.escape(this.editorTarget.getAttribute('input'))}`);
  }

  disable() {
    if (!this.hasEditorTarget) return;
    this.editorTarget.setAttribute('contenteditable', 'false');
  }

  toggleMenu() {
    if (this.formatMenuTarget.hidden) this.openMenu();
    else this.closeMenu();
  }

  openMenu() {
    this.formatMenuTarget.hidden = false;
    this.formatTriggerTarget.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.closeOnOutside);
    const selected = this.formatOptionTargets.find(o => o.getAttribute('aria-selected') === 'true');
    (selected || this.formatOptionTargets[0]).focus();
  }

  closeMenu() {
    this.formatMenuTarget.hidden = true;
    this.formatTriggerTarget.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.closeOnOutside);
  }

  closeMenuAndRefocus() {
    this.closeMenu();
    this.formatTriggerTarget.focus();
  }

  onMenuKeydown(event) {
    const options = this.formatOptionTargets;
    const current = options.indexOf(document.activeElement);
    const focusAt = index => { event.preventDefault(); options[index].focus(); };

    if (event.key === 'ArrowDown') focusAt(Math.min(current + 1, options.length - 1));
    else if (event.key === 'ArrowUp') focusAt(Math.max(current - 1, 0));
    else if (event.key === 'Home') focusAt(0);
    else if (event.key === 'End') focusAt(options.length - 1);
    else if (event.key === 'Escape') { event.preventDefault(); this.closeMenuAndRefocus(); }
  }

  closeOnOutside(event) {
    if (this.formatTriggerTarget.contains(event.target)) return;
    if (this.formatMenuTarget.contains(event.target)) return;
    this.closeMenu();
  }

  // Trix listens for mousedown on its toolbar buttons and ignores click, so
  // Enter and Space are replayed as a mousedown on the button itself.
  activateToolbarButton(event) {
    event.preventDefault();
    event.currentTarget.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
  }

  openLink(event) {
    event.preventDefault();
    if (!this.editorTarget.editor) return;
    if (!this.linkPopoverTarget.hidden) {
      this.closeLink();
      return;
    }

    const { editor } = this.editorTarget;
    this.linkRange = editor.getSelectedRange();
    const [start, end] = this.linkRange;
    this.linkTextTarget.value = editor.getDocument().toString().slice(start, end).replace(/\n+$/, '');
    this.linkUrlTarget.value = this.existingHref(this.linkRange);

    this.linkPopoverTarget.hidden = false;
    this.linkButtonTarget.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.closeLinkOnOutside);
    (this.linkTextTarget.value ? this.linkUrlTarget : this.linkTextTarget).focus();
  }

  applyLink(event) {
    if (event) event.preventDefault();
    const raw = this.linkUrlTarget.value.trim();
    if (!raw) {
      this.closeLink();
      return;
    }

    const url = safeLinkUrl(raw);
    if (!url) {
      this.linkUrlTarget.focus();
      return;
    }

    const { editor } = this.editorTarget;
    const [start] = this.linkRange;
    const text = this.linkTextTarget.value || url;
    editor.setSelectedRange(this.linkRange);
    editor.insertString(text);
    editor.setSelectedRange([start, start + text.length]);
    editor.activateAttribute('href', url);
    editor.setSelectedRange([start + text.length, start + text.length]);

    this.closeLink();
    this.editorTarget.focus();
  }

  closeLink() {
    this.linkPopoverTarget.hidden = true;
    this.linkButtonTarget.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.closeLinkOnOutside);
  }

  closeLinkOnOutside(event) {
    if (this.linkButtonTarget.contains(event.target)) return;
    if (this.linkPopoverTarget.contains(event.target)) return;
    this.closeLink();
  }

  onLinkKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeLink();
      this.linkButtonTarget.focus();
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.applyLink();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusables = Array.from(this.linkPopoverTarget.querySelectorAll('input, button'))
      .filter(el => !el.disabled);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  existingHref(range) {
    try {
      return this.editorTarget.editor.getDocument().getCommonAttributesAtRange(range).href || '';
    } catch (error) {
      return '';
    }
  }

  selectFormat(event) {
    const { format } = event.currentTarget.dataset;
    this.applyFormat(format);
    this.setActiveFormat(format);
    this.closeMenu();
    this.formatTriggerTarget.focus();
    this.save();
  }

  applyFormat(format) {
    if (!this.hasEditorTarget || !this.editorTarget.editor) return;

    const { editor } = this.editorTarget;
    HEADINGS.forEach(heading => {
      if (editor.attributeIsActive(heading)) editor.deactivateAttribute(heading);
    });
    if (format !== 'normal') editor.activateAttribute(format);
  }

  syncFormat() {
    if (!this.hasEditorTarget || !this.editorTarget.editor) return;

    const { editor } = this.editorTarget;
    const active = HEADINGS.find(heading => editor.attributeIsActive(heading)) || 'normal';
    this.setActiveFormat(active);
  }

  // Trix flags the active button with data-trix-active, which only drives the
  // styling. Mirroring it covers every route in one place — mouse, keyboard and
  // Trix's own Ctrl+B shortcuts all end up setting that attribute.
  // Trix binds these inside the editor, so nothing on screen hints they exist.
  // The modifier only resolves client-side, hence not doing this in the template.
  labelShortcuts() {
    const isMac = /Mac|iP(hone|ad|od)/.test(navigator.platform || navigator.userAgent || '');
    this.toolbarToggleTargets.forEach((button) => {
      const key = button.dataset.trixKey;
      if (!key) return;

      const upper = key.toUpperCase();
      button.title = `${button.getAttribute('aria-label')} (${isMac ? '\u2318' : 'Ctrl+'}${upper})`;
      button.setAttribute('aria-keyshortcuts', `${isMac ? 'Meta' : 'Control'}+${upper}`);
    });
  }

  syncToolbarToggles() {
    this.toolbarToggleTargets.forEach((button) => {
      button.setAttribute('aria-pressed', button.hasAttribute('data-trix-active') ? 'true' : 'false');
    });
  }

  setActiveFormat(format) {
    const option = this.formatOptionTargets.find(target => target.dataset.format === format);
    if (option) {
      if (this.hasFormatLabelTarget) this.formatLabelTarget.textContent = option.dataset.label;
      const icon = option.querySelector('.ui-rte__format-option-icon');
      if (this.hasFormatIconTarget && icon) this.formatIconTarget.innerHTML = icon.outerHTML;
    }
    this.formatOptionTargets.forEach(target =>
      target.setAttribute('aria-selected', String(target.dataset.format === format)));
  }
}
